/// <reference types="w3c-web-usb" />
import { useState } from "react";
import {
  Printer,
  Text,
  Row,
  Line,
  Br,
  Cut,
  render,
} from "react-thermal-printer";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../utils/urls";
import api from "../utils/api";

type FinalizeBillItem = {
  id: string | null;
  storeName: string | null;
  name: string;
  quantity: number;
  price: number;
  total: number;
  customerId: string | null;
  paid: boolean;
  note: string | null;
  addOn: boolean;
};

type FinalizeBillModalProps = {
  isOpen: boolean;
  finalizedItems: FinalizeBillItem[];
  onClose: () => void;
  userData: UserData;
};

type UserData = {
  userId: string;
  storeName: string | null;
  username: string;
  address: string | null;
  GST: string | null;
  phonenumber: string | null;
};

const FinalizeBillModal = ({
  isOpen,
  finalizedItems,
  onClose,
  userData,
}: FinalizeBillModalProps) => {
  const [loading, setLoading] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);

  const total = finalizedItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const splitTextToLines = (text: string, maxLength: number): string[] => {
    const words = text.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      if ((currentLine + word).length <= maxLength) {
        currentLine += word + " ";
      } else {
        lines.push(currentLine.trim());
        currentLine = word + " ";
      }
    }

    if (currentLine.trim()) {
      lines.push(currentLine.trim());
    }

    return lines;
  };

  const createReceiptComponent = () => (
    <Printer type="epson" width={32} characterSet="pc437_usa">
      <Text align="center" bold={true}>
        {userData.storeName || userData.username}
      </Text>
      {splitTextToLines(userData.address || "", 32).map((line, idx) => (
        <Text key={idx} align="center">
          {line}
        </Text>
      ))}
      <Text align="center">{userData.phonenumber}</Text>
      <Text align="center">{userData.GST}</Text>
      <Br />
      <Line />
      {finalizedItems.map((item, idx) => (
        <Row
          key={idx}
          left={`${item.name} x ${item.quantity}`}
          right={`Rs ${(item.price * item.quantity).toFixed(2)}`}
        />
      ))}
      <Line />
      <Row left="Total" right={`Rs ${total.toFixed(2)}`} />

      {/* Add discount if present */}
      {discountAmount > 0 && (
        <Row left="Discount" right={`-Rs ${discountAmount.toFixed(2)}`} />
      )}
      {discountPercentage > 0 && (
        <Row left="Discount %" right={`${discountPercentage}%`} />
      )}

      {/* Add CGST and SGST (2.5% each) */}
      <Row
        left="C GST (2.5%)"
        right={`Rs ${(
          (total -
            (discountPercentage > 0
              ? (total * discountPercentage) / 100
              : discountAmount)) *
          0.025
        ).toFixed(2)}`}
      />
      <Row
        left="S GST (2.5%)"
        right={`Rs ${(
          (total -
            (discountPercentage > 0
              ? (total * discountPercentage) / 100
              : discountAmount)) *
          0.025
        ).toFixed(2)}`}
      />

      {/* Final Total after GST and Discount */}
      <Line />
      <Row
        left="Final Total"
        right={`Rs ${(
          total -
          (discountPercentage > 0
            ? (total * discountPercentage) / 100
            : discountAmount) +
          (total -
            (discountPercentage > 0
              ? (total * discountPercentage) / 100
              : discountAmount)) *
            0.025 *
            2
        ).toFixed(2)}`}
      />

      <Br />
      <Text align="center">Thank you! Visit again.</Text>
      <Cut />
    </Printer>
  );

  const sendToPrinter = async (data: Uint8Array) => {
    const savedDevice = JSON.parse(
      localStorage.getItem("selectedPrinter") || "{}"
    );

    let device;
    if (
      savedDevice.vendorId &&
      savedDevice.productId &&
      savedDevice.serialNumber
    ) {
      try {
        device = await navigator.usb.requestDevice({
          filters: [
            {
              vendorId: savedDevice.vendorId,
              productId: savedDevice.productId,
            },
          ],
        });
      } catch {
        device = null;
      }
    }

    if (!device) {
      device = await navigator.usb.requestDevice({ filters: [{}] });
      localStorage.setItem(
        "selectedPrinter",
        JSON.stringify({
          vendorId: device.vendorId,
          productId: device.productId,
          serialNumber: device.serialNumber,
        })
      );
    }

    await device.open();
    await device.selectConfiguration(1);
    await device.claimInterface(0);

    const endpoint =
      device?.configuration?.interfaces[0].alternates[0].endpoints.find(
        (e) => e.direction === "out"
      )?.endpointNumber || 3;

    await device.transferOut(endpoint, data);
    await device.close();
  };

  const handleSaveAndPrint = async () => {
    setLoading(true);
    try {
      const totalAfterDiscount =
        total -
        (discountPercentage > 0
          ? (total * discountPercentage) / 100
          : discountAmount);
      const response = await api.post(API_ENDPOINTS.BILLING.SAVE_BILL, {
        items: finalizedItems.map((item) => ({
          id: item.id || "",
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.price * item.quantity,
          addOn: item.addOn,
        })),
        totalAmount: (totalAfterDiscount + totalAfterDiscount * 0.05).toFixed(
          2
        ),
        paymentMethod: "self",
        customerId: null,
        paid: false,
        note: null,
        discountAmount,
        discountPercentage,
        amountBeforeDiscountandGst: total,
      });
      console.log("RESPONSE" + response.data);
      if (response.data.error) throw new Error("Failed to save bill.");

      const data = await render(createReceiptComponent());
      await sendToPrinter(data);

      toast.success("Bill saved and printed!");
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save or print bill.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const totalAfterDiscount =
        total -
        (discountPercentage > 0
          ? (total * discountPercentage) / 100
          : discountAmount);
      const response = await api.post(API_ENDPOINTS.BILLING.SAVE_BILL, {
        items: finalizedItems.map((item) => ({
          id: item.id || "",
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.price * item.quantity,
          addOn: item.addOn,
        })),
        totalAmount: (totalAfterDiscount + totalAfterDiscount * 0.05).toFixed(
          2
        ),
        paymentMethod: "self",
        customerId: null,
        paid: false,
        note: null,
        discountAmount,
        discountPercentage,
        amountBeforeDiscountandGst: total,
      });
      if (response.data.error) throw new Error("Failed to save bill.");
      toast.success("Bill saved!");
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save or print bill.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Bill Preview</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black text-sm"
          >
            ✕
          </button>
        </div>

        <div className="text-sm">
          <div className="text-center mb-2 font-bold text-base">
            {userData.storeName || userData.username}
            <div className="text-xs font-normal">{userData.address}</div>
            <div className="text-xs font-normal">{userData.phonenumber}</div>
            <div className="text-xs font-normal">{userData.GST}</div>
          </div>
          <hr className="my-2" />

          {finalizedItems.map((item, idx) => (
            <div key={idx} className="flex justify-between py-1">
              <span>
                {item.name} x {item.quantity}
              </span>
              <span>Rs {(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}

          <hr className="my-2" />
          <div className="flex justify-between font-bold">
            <span>Total</span>
            <span>Rs {total.toFixed(2)}</span>
          </div>

          {/* Discount Section */}
          {(discountAmount > 0 || discountPercentage > 0) && (
            <div className="flex justify-between">
              <span>Discount</span>
              <span>
                {discountPercentage > 0
                  ? `(${discountPercentage}%) - Rs ${(
                      (total * discountPercentage) /
                      100
                    ).toFixed(2)}`
                  : `Rs ${discountAmount.toFixed(2)}`}
              </span>
            </div>
          )}

          {/* GST Section */}
          <div className="flex justify-between">
            <span>CGST (2.5%)</span>
            <span>
              Rs{" "}
              {(
                (total -
                  (discountPercentage > 0
                    ? (total * discountPercentage) / 100
                    : discountAmount)) *
                0.025
              ).toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>SGST (2.5%)</span>
            <span>
              Rs{" "}
              {(
                (total -
                  (discountPercentage > 0
                    ? (total * discountPercentage) / 100
                    : discountAmount)) *
                0.025
              ).toFixed(2)}
            </span>
          </div>

          <hr className="my-2" />
          <div className="flex justify-between font-bold">
            <span>Grand Total</span>
            <span>
              Rs{" "}
              {(
                total -
                (discountPercentage > 0
                  ? (total * discountPercentage) / 100
                  : discountAmount) +
                (total -
                  (discountPercentage > 0
                    ? (total * discountPercentage) / 100
                    : discountAmount)) *
                  0.025 *
                  2
              ).toFixed(2)}
            </span>
          </div>

          <div className="text-center text-xs mt-4">
            Thank you! Visit again.
          </div>
        </div>

        <div className="mt-6">
          <div className="flex gap-4 mb-4">
            <div className="flex flex-col">
              <label
                htmlFor="discountAmount"
                className="text-sm text-gray-700 mb-1"
              >
                Discount Amount
              </label>
              <input
                type="number"
                id="discountAmount"
                name="discountAmount"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(Number(e.target.value))}
                className="border border-gray-300 px-3 py-2 rounded"
                disabled={loading}
              />
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="discountPercentage"
                className="text-sm text-gray-700 mb-1"
              >
                Discount Percentage (%)
              </label>
              <input
                type="number"
                id="discountPercentage"
                name="discountPercentage"
                value={discountPercentage}
                onChange={(e) => setDiscountPercentage(Number(e.target.value))}
                className="border border-gray-300 px-3 py-2 rounded"
                disabled={loading}
              />
            </div>
          </div>

          <div className="flex gap-4 mb-4">
            <div className="flex flex-col">
              <button
                // onClick={handleSave}
                className="bg-blue-600 text-white px-4 py-2 rounded"
                disabled={true}
              >
                {loading ? "Processing..." : "Send Payment Link"}
              </button>
            </div>
            <div className="flex flex-col">
              <button
                // onClick={handleSave}
                className="bg-blue-600 text-white px-4 py-2 rounded"
                disabled={true}
              >
                {loading ? "Processing..." : "Pay Now"}
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="bg-green-600 text-white px-4 py-2 rounded"
              disabled={loading}
            >
              {loading ? "Processing..." : "Save"}
            </button>
            <button
              onClick={handleSaveAndPrint}
              className="bg-green-600 text-white px-4 py-2 rounded"
              disabled={loading}
            >
              {loading ? "Processing..." : "Save & Print"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalizeBillModal;
